"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";

interface TypingTextProps {
	text: string;
	className?: string;
	delay?: number;
	speed?: number;
	showCursor?: boolean;
	cursorChar?: string;
}

export function BlinkingDot({
	delay = 0,
	char = ".",
}: {
	delay?: number;
	char?: string;
}) {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, amount: 0.2 });

	return (
		<motion.span
			ref={ref}
			initial={{ opacity: 0 }}
			animate={isInView ? { opacity: 1 } : {}}
			transition={{ delay, duration: 0 }}
			className="inline-block"
		>
			<motion.span
				animate={{ opacity: [1, 0] }}
				transition={{
					duration: 0.8,
					repeat: Infinity,
					repeatType: "reverse",
				}}
				className="text-primary inline-block"
			>
				{char}
			</motion.span>
		</motion.span>
	);
}

export function TypingText({
	text,
	className = "",
	delay = 0,
	speed = 0.03,
	showCursor = true,
	cursorChar = "●",
}: TypingTextProps) {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, amount: 0.2 });

	return (
		<span ref={ref} className={`inline ${className}`}>
			{text.split("").map((char, index) => (
				<motion.span
					// biome-ignore lint/suspicious/noArrayIndexKey: Static text animation
					key={index}
					initial={{ opacity: 0 }}
					animate={isInView ? { opacity: 1 } : {}}
					transition={{ duration: 0, delay: delay + index * speed }}
				>
					{char}
				</motion.span>
			))}

			{showCursor && (
				<motion.span
					initial={{ opacity: 0 }}
					animate={isInView ? { opacity: 1 } : {}}
					transition={{ delay: delay + text.length * speed, duration: 0 }}
					className="inline-block"
				>
					<motion.span
						animate={{ opacity: [1, 0] }}
						transition={{
							duration: 0.8,
							repeat: Infinity,
							repeatType: "reverse",
						}}
						className="text-primary inline-block ml-0.5"
					>
						{cursorChar}
					</motion.span>
				</motion.span>
			)}
		</span>
	);
}
